import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Open/closed state for a trigger-anchored popover that closes the way every
 * other popover on the web closes.
 *
 * The notices menu, the user menu and the capacity panel were each a bare
 * boolean whose only close path was pressing the trigger again. Click anywhere
 * else and the panel stayed up over the work surface; press Escape and nothing
 * happened. The two scrimmed dialogs already had an Escape listener
 * (AccountDrawer, Enroll) — these three had nothing.
 *
 * Returns a ref for the WRAPPER, not the panel: the trigger has to count as
 * inside, or pressing it would close and immediately reopen.
 */
export function usePopover<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const [open, setOpen] = useState(false);

  /**
   * Dismiss and put the caret back on the trigger.
   *
   * The trigger is found from the DOM rather than remembered from
   * `document.activeElement` at open time. Reading it there meant capturing it
   * inside a `setState` updater — a side effect in a function React is free to
   * call twice — and it was wrong anyway whenever the panel was opened by a
   * pointer, since a click does not always focus the button it hits.
   *
   * Only Escape and explicit callers route through here. Outside-click
   * dismissal deliberately does NOT: focus belongs wherever the user just
   * clicked, and dragging it back to the trigger would fight them.
   */
  const close = useCallback(() => {
    setOpen(false);
    ref.current?.querySelector<HTMLElement>('button')?.focus();
  }, []);

  const toggle = useCallback(() => setOpen((v) => !v), []);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      /*
       * `stopImmediatePropagation`, because Dialog binds its Escape handler to
       * `document` in the capture phase too and `stopPropagation` cannot stop a
       * sibling listener on the same node in the same phase — dismissing this
       * menu would also have closed the surface behind it.
       */
      e.stopImmediatePropagation();
      close();
    };

    // Capture on pointerdown, not click: a click that lands on a control which
    // unmounts itself never bubbles, and the menu would stay open behind it.
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open, close]);

  return { ref, open, setOpen, toggle, close };
}
